import { Buttons, Images } from "@/components";
import { Spinner } from '@nextui-org/spinner';
import useMint, { MAX_ITEM_FREE, MAX_ITEM_MINT, MAX_ITEM_PHASE_2, MAX_ITEM_PHASE_3, PRICE_MINT_FREE, PRICE_MINT_PHASE_2, PRICE_MINT_PHASE_3 } from "./useMint";
import { formatCurrency } from "@/utils";
import { useRef } from "react";
import useFetch from "./useFetch";
import { useAuth } from "@/provider/UserProvider";
import { useRouter } from "@/provider/RouterProvider";

const EventCollection = () => {
  const inputRef = useRef<any>();
  const isFinish = true;
  const { isLoginClient } = useAuth();
  const router = useRouter();
  const { data, refresh, userTotalMint, totalMinted } = useFetch();

  const isFirstMint =  Number(userTotalMint) === 0;
  const isFree = (Number(totalMinted) < MAX_ITEM_FREE);
  const isPhase2 = MAX_ITEM_FREE <= Number(totalMinted) && Number(totalMinted) < MAX_ITEM_PHASE_2 + MAX_ITEM_FREE;

  const getPhase = () => {
    if (isFree) return '1';
    if (isPhase2) return '2';
    return '3';
  }

  const currentPhase = getPhase();

  const { 
    handleSubmit, 
    totalPrice, 
    upQuantity, 
    downQuantity, 
    handleChangeQuantity,
    loading,
  } = useMint(refresh, currentPhase);

  const priceCurrentMint = () => {
    if (isFree) return PRICE_MINT_FREE;
    if (isPhase2) return PRICE_MINT_PHASE_2;
    return PRICE_MINT_PHASE_3;
  }

  if (!data) return (
    <div className="flex items-center justify-center h-[300px]">
      <Spinner color="default" />
    </div>
  );

  return (
    <div className="mx-[40px] md:mx-[20px] mb-8">
      <div className="flex xl:flex-wrap border-1 border-gray-200 rounded-[20px] overflow-hidden">
        <div className="w-[calc(100%-700px)] 2xl:w-[calc(100%-600px)] xl:w-[100%] xl:h-[450px] sm:h-[250px]">
          <div className="w-full relative h-full overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full bg-[#130b0a]"></div>
            <div onClick={() => router.push(`/collection/${data.id}`)} className="absolute cursor-pointer top-0 left-0 w-full h-full">
              <Images className="object-contain w-full" alt="event" src={data.miniCover} />
            </div>
          </div>
        </div>
        <div className="w-[700px] 2xl:w-[600px] xl:w-[100%]">
          <div className="px-[40px] pt-[30px] pb-[40px] sm:px-4 sm:py-4">
            <div className="flex items-center justify-between max-w-[500px]">
              <div onClick={() => router.push(`/collection/${data.id}`)} className="text-[24px] cursor-pointer sm:text-[18px] leading-[1.3] font-semibold line-clamp-2">{data.name}</div>
            </div>
            <div className="flex items-center justify-between flex-wrap">
              <p className="text-gray-700 text-[15px] sm:text-[14px] mt-2">
                <span className="font-semibold">Quantity of Mint: </span> {totalMinted}/{MAX_ITEM_MINT}
              </p>
              {isLoginClient && (
                <p className="text-gray-700 text-[15px] sm:text-[14px] mt-2">
                  <span className="font-semibold">Your NFT number: </span> {userTotalMint}
                </p>
              )}
            </div>
            <p className="text-gray-700 text-[15px] sm:text-[14px] mt-2 leading-[1.6]">
              <span className="font-semibold">Description: </span>{data.description}
            </p>
            <p className="text-[14px] text-orange-500">* When you own Samorai Coders NFTs, you will be able to use all services for free.</p>
            <div className="bg-gray-200 h-[1px] w-full my-4"></div>
            <p className="font-semibold sm:text-[15px]">Public Mint</p>
            <div>
              <p aria-checked={currentPhase === '1'} className="text-[14px] text-gray-300 aria-checked:text-black sm:text-[13px]">* Phase 1: Free minting for the first {MAX_ITEM_FREE} NFTs. Each user can only mint 1 free NFT.</p>
              <p aria-checked={currentPhase === '2'} className="text-[14px] text-gray-300 aria-checked:text-black sm:text-[13px]">* Phase 2: Mint {MAX_ITEM_PHASE_2} NFTs, with a minting price of {PRICE_MINT_PHASE_2} AIRI.</p>
              <p aria-checked={currentPhase === '3'} className="text-[14px] text-gray-300 aria-checked:text-black sm:text-[13px]">* Phase 3: Mint {MAX_ITEM_PHASE_3} NFTs, with a minting price of {PRICE_MINT_PHASE_3} AIRI.</p>
            </div>
            {!isFree && (
              <div className="mt-2 flex justify-between sm:flex-wrap">
                <div className="sm:w-full sm:mb-2">
                  <p className="text-gray-500 font-medium text-[15px] sm:text-[14px] mb-2 sm:mb-1">Price</p>
                  <p className="font-semibold text-[26px] sm:text-[14px]">{formatCurrency(priceCurrentMint())} AIRI</p>
                </div>
                <div className="sm:w-full">
                  <p className="text-gray-500 font-medium sm:text-[14px] text-[15px] mb-2">Quantity</p>
                  <div className="flex border-[#CCD1E0] rounded-lg border-1 sm:inline-flex">
                    <div onClick={() => downQuantity(inputRef)} className="w-[36px] h-[36px] flex items-center justify-center text-[25px] cursor-pointer">-</div>
                    <input ref={inputRef} onChange={(e) => handleChangeQuantity(e.target.value, inputRef)} className="text-center outline-none w-[80px] leading-[1] font-semibold text-[17px] sm:text-[15px]" defaultValue="1" type="number" />
                    <div onClick={() => upQuantity(inputRef)} className="w-[36px] h-[36px] flex items-center justify-center text-[22px] cursor-pointer">+</div>
                  </div>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between mt-5 p-4 sm:p-3 rounded-lg bg-[#FEF2D3]">
              <p className="text-[17px] sm:text-[14px]">{isFree ? 'Price:' : 'Total:'} <span className="font-semibold">{isFree ? 'FREE' : `${formatCurrency(totalPrice)} AIRI`}</span></p>
              {(!isFirstMint && isFree) || isFinish ? (
                <Buttons typeBtn="disabled">
                {isFree ? 'Minted' : 'Ended'}
                </Buttons>
              ) : (
                <Buttons 
                  isLoading={loading}
                  onClick={() => handleSubmit({
                    isFinish, 
                    collection: data.id,
                    countItems: Number(totalMinted),
                    isFree,
                  })} 
                  className="sm:text-[13px]"
                >
                  Mint Now
                </Buttons>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EventCollection;